/**
 * Segment Versions Management Component
 * Handles upload, snapshot, restore, and listing of segment mapping versions
 */

import { useEffect, useState } from "react";
import { useStore } from "zustand";
import type { TAppStore } from "../../../store";
import type {
    TSegmentVersion,
    TSegmentVersionState,
} from "../../../store/segmentVersions/sliceSegmentVersions";

export type TSegmentVersionActions = {
    fetchVersions: (dealId: string, segmentType: number) => Promise<void>;
    uploadVersion: (
        dealId: string,
        segmentType: number,
        file: File,
        description?: string,
    ) => Promise<unknown>;
    createSnapshot: (dealId: string, segmentType: number, description?: string) => Promise<unknown>;
    restoreVersion: (dealId: string, segmentType: number, versionId: string) => Promise<unknown>;
};

export type TOrganismSegmentVersionsProps = {
    dealId: string;
    segmentType: number;
    store: TAppStore;
    segmentVersionActions: TSegmentVersionActions;
    onVersionChange?: () => void;
    canUpload?: boolean;
    canSnapshot?: boolean;
    canRestore?: boolean;
};

const defaultSegmentState: TSegmentVersionState = {
    versionsListState: { status: "idle", data: null, error: null },
    versionUploadState: { status: "idle", data: null, error: null },
    snapshotState: { status: "idle", data: null, error: null },
    restoreState: { status: "idle", data: null, error: null },
};

export const OrganismSegmentVersions = ({
    dealId,
    segmentType,
    store,
    segmentVersionActions,
    onVersionChange,
    canUpload = true,
    canSnapshot = true,
    canRestore = true,
}: TOrganismSegmentVersionsProps) => {
    const [selectedVersion, setSelectedVersion] = useState<TSegmentVersion | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showSnapshotModal, setShowSnapshotModal] = useState(false);
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

    // Subscribe to per-segment store state
    const segState = useStore(
        store,
        (state) => state.segmentVersionsState[segmentType] ?? defaultSegmentState,
    );
    const { versionsListState, versionUploadState, snapshotState, restoreState } = segState;

    // Fetch versions on mount
    useEffect(() => {
        if (dealId) {
            segmentVersionActions.fetchVersions(dealId, segmentType);
        }
    }, [dealId, segmentType, segmentVersionActions]);

    // Handle successful operations
    useEffect(() => {
        if (versionUploadState.status === "fulfilled" && versionUploadState.data?.success) {
            setShowUploadModal(false);
            segmentVersionActions.fetchVersions(dealId, segmentType);
            if (onVersionChange) onVersionChange();
            store.getState().resetSegmentVersionUploadState(segmentType);
        }
    }, [versionUploadState, dealId, segmentType, segmentVersionActions, onVersionChange, store]);

    useEffect(() => {
        if (snapshotState.status === "fulfilled" && snapshotState.data?.success) {
            setShowSnapshotModal(false);
            segmentVersionActions.fetchVersions(dealId, segmentType);
            store.getState().resetSegmentSnapshotState(segmentType);
        }
    }, [snapshotState, dealId, segmentType, segmentVersionActions, store]);

    useEffect(() => {
        if (restoreState.status === "fulfilled" && restoreState.data?.success) {
            setShowRestoreConfirm(false);
            setSelectedVersion(null);
            segmentVersionActions.fetchVersions(dealId, segmentType);
            if (onVersionChange) onVersionChange();
            store.getState().resetSegmentRestoreState(segmentType);
        }
    }, [restoreState, dealId, segmentType, segmentVersionActions, onVersionChange, store]);

    const handleUpload = async (file: File, description?: string) => {
        try {
            await segmentVersionActions.uploadVersion(dealId, segmentType, file, description);
        } catch (error) {
            console.error("Upload failed:", error);
        }
    };

    const handleCreateSnapshot = async (description?: string) => {
        try {
            await segmentVersionActions.createSnapshot(dealId, segmentType, description);
        } catch (error) {
            console.error("Snapshot failed:", error);
        }
    };

    const handleRestore = async () => {
        if (!selectedVersion) return;
        try {
            await segmentVersionActions.restoreVersion(dealId, segmentType, selectedVersion.id);
        } catch (error) {
            console.error("Restore failed:", error);
        }
    };

    const versions = versionsListState.data?.items || [];
    const isLoading =
        versionsListState.status === "pending" ||
        versionUploadState.status === "pending" ||
        snapshotState.status === "pending" ||
        restoreState.status === "pending";

    return (
        <div className="space-y-4">
            {/* Action Buttons */}
            <div className="flex gap-2 items-center">
                {canUpload && (
                    <button
                        type="button"
                        onClick={() => setShowUploadModal(true)}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Upload new version
                    </button>
                )}

                {canSnapshot && (
                    <button
                        type="button"
                        onClick={() => setShowSnapshotModal(true)}
                        disabled={isLoading}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Create new version
                    </button>
                )}

                <div className="flex-1" />

                {canRestore && (
                    <>
                        {/* Version Selector */}
                        <select
                            value={selectedVersion?.id || ""}
                            onChange={(e) => {
                                const version = versions.find((v) => v.id === e.target.value);
                                setSelectedVersion(version || null);
                            }}
                            disabled={isLoading || versions.length === 0}
                            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">Select...</option>
                            {versions.map((v) => (
                                <option key={v.id} value={v.id}>
                                    V{v.versionNumber} - {v.sourceType} by{" "}
                                    {v.createdByName || "Unknown"} (
                                    {new Date(v.createdAt).toLocaleDateString()})
                                </option>
                            ))}
                        </select>

                        <button
                            type="button"
                            onClick={() => setShowRestoreConfirm(true)}
                            disabled={isLoading || !selectedVersion}
                            className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Apply selected version
                        </button>
                    </>
                )}
            </div>

            {/* Error Messages */}
            {versionUploadState.status === "rejected" && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    Upload failed: {versionUploadState.error}
                </div>
            )}
            {snapshotState.status === "rejected" && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    Snapshot failed: {snapshotState.error}
                </div>
            )}
            {restoreState.status === "rejected" && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    Restore failed: {restoreState.error}
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <UploadModal
                    onUpload={handleUpload}
                    onClose={() => setShowUploadModal(false)}
                    isLoading={versionUploadState.status === "pending"}
                />
            )}

            {/* Snapshot Modal */}
            {showSnapshotModal && (
                <SnapshotModal
                    onCreate={handleCreateSnapshot}
                    onClose={() => setShowSnapshotModal(false)}
                    isLoading={snapshotState.status === "pending"}
                />
            )}

            {/* Restore Confirmation */}
            {showRestoreConfirm && selectedVersion && (
                <RestoreConfirmModal
                    onConfirm={handleRestore}
                    onCancel={() => setShowRestoreConfirm(false)}
                    isLoading={restoreState.status === "pending"}
                />
            )}
        </div>
    );
};

// Upload Modal Component
const UploadModal = ({
    onUpload,
    onClose,
    isLoading,
}: {
    onUpload: (file: File, description?: string) => void;
    onClose: () => void;
    isLoading: boolean;
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [description, setDescription] = useState("");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (file) {
            onUpload(file, description || undefined);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">Upload new version</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Excel File (.xlsx)
                        </label>
                        <input
                            type="file"
                            accept=".xlsx"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            required
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description (optional)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Describe this version..."
                        />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!file || isLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? "Uploading..." : "Upload"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Snapshot Modal Component
const SnapshotModal = ({
    onCreate,
    onClose,
    isLoading,
}: {
    onCreate: (description?: string) => void;
    onClose: () => void;
    isLoading: boolean;
}) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">Confirm create new version</h2>
                <p className="text-gray-700 mb-4">
                    Are you sure you would like to create new version from the actual state? As a
                    reminder, please generate a new version only when you finished all your changes.
                </p>
                <div className="flex gap-2 justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => onCreate()}
                        disabled={isLoading}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Creating..." : "Confirm"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Restore Confirmation Modal
const RestoreConfirmModal = ({
    onConfirm,
    onCancel,
    isLoading,
}: {
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
}) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">Confirm apply selected version</h2>
                <p className="text-gray-700 mb-4">
                    You requested to revert the mappings to a previous version.
                    <br />
                    By confirming your decision, you consent on
                </p>
                <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-1">
                    <li>
                        The mappings will be reverted to a previously uploaded version chosen by
                        you.
                    </li>
                    <li>All changes made after the selected version will be lost.</li>
                </ol>
                <p className="text-gray-700 font-medium mb-4">Do you confirm your request?</p>
                <div className="flex gap-2 justify-end">
                    <button
                        onClick={onCancel}
                        disabled={isLoading}
                        className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Applying..." : "Confirm"}
                    </button>
                </div>
            </div>
        </div>
    );
};
